import type { EditorContentVersion } from '../../types';
import { Button, ModalBody, ModalFooter, ModalHeader } from '@workramp/ui';
import { useCallback, useEffect, useState } from 'react';
import { I18n } from '~/common/explicit-exports';
import { useEditorVersionHistoryQuery, useEditorVersionContentQuery } from '../../api';
import { GlobalStyle, StyledModal } from './EditorVersionModal.styles';
import { VersionViewer } from './VersionViewer';

export interface EditorVersionModalProps {
  id: string;
  isOpen: boolean;
  onSave: (versionContent: string) => void;
  onClose: () => void;
}

export function EditorVersionModal({ id, isOpen, onSave, onClose }: EditorVersionModalProps) {
  const TRANSLATED_CANCEL = I18n.t('js.common.modal.cancel');
  const RESTORE_THIS_VERSION = 'Restore this version';

  const [selectedVersion, setSelectedVersion] = useState<EditorContentVersion | null>(null);

  const versionHistoryQuery = useEditorVersionHistoryQuery({ id });
  const versionContentQuery = useEditorVersionContentQuery(
    { url: selectedVersion?.url ?? '' },
    { enabled: !!selectedVersion },
  );

  useEffect(() => {
    // Default to the most recent version if no version is selected
    if (versionHistoryQuery.data && !selectedVersion) {
      const mostRecentVersion = versionHistoryQuery.data.list[0];
      if (mostRecentVersion) {
        setSelectedVersion(mostRecentVersion);
      }
    }
  }, [selectedVersion, versionHistoryQuery.data]);

  const handleRestore = useCallback(() => {
    if (versionContentQuery.data) {
      onSave(versionContentQuery.data);
    }

    onClose();
  }, [versionContentQuery.data, onClose, onSave]);

  return (
    <StyledModal isOpen={isOpen} onClose={onClose} zIndex={1400} size="md">
      {isOpen && <GlobalStyle />}
      <ModalHeader title="Version History" />
      <ModalBody>
        <VersionViewer
          isLoading={versionHistoryQuery.isLoading || (selectedVersion && versionContentQuery.isLoading)}
          content={versionContentQuery.data}
          selectedVersion={selectedVersion}
          versions={versionHistoryQuery.data?.list ?? []}
          onChange={setSelectedVersion}
        />
      </ModalBody>
      <ModalFooter
        actions={
          <>
            <Button color="secondary" variant="text" onPress={onClose}>
              {TRANSLATED_CANCEL}
            </Button>
            <Button onPress={handleRestore}>{RESTORE_THIS_VERSION}</Button>
          </>
        }
      />
    </StyledModal>
  );
}
